import moment from "moment";
import { parseTimeOfDay } from "@/shared/util/datetime.ts";

const COMMAS_NUMBER_FORMAT = new Intl.NumberFormat("en-US")

export function numberWithCommas(x: number): string {
  return COMMAS_NUMBER_FORMAT.format(x)
}

const DOLLARS_NUMBER_FORMAT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function numberToDollars(x: number): string {
  return DOLLARS_NUMBER_FORMAT.format(x)
}

/**
 * Parse an integer, returning undefined if result is NaN or Infinity,
 * passing null or undefined through as-is.
 *
 * @param {string|null|undefined} x the string to parse
 * @returns {number|null|undefined} the result of calling parseInt on x, undefined if result is NaN or Infinity, or x if x is null or undefined
 */
export function parseIntLenient(x?: string | null): number | undefined | null {
  if (x === null || x === undefined) {
    return x;
  }

  const parsed = parseInt(x);
  if (isNaN(parsed) || parsed === Infinity) {
    return undefined;
  }

  return parsed;
}

/**
 * Pluralize a word based on a count.
 *
 * @param {number} count the count
 * @param {string} singular the singular form
 * @param {string} [plural] the plural form, if not provided, singular + "s" will be used
 * @returns {string} the pluralized word
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  if (!plural) {
    plural = singular + "s"
  }
  return count === 1 ? singular : plural
}

/**
 * Returns true if every code point in `value` is in the ASCII range (U+0000–U+007F).
 * Suitable for validating text that must be ASCII-only (e.g. downstream systems, legacy encodings).
 */
export function isAsciiOnlyString(value: string): boolean {
  for (const ch of value) {
    const cp = ch.codePointAt(0);
    if (cp === undefined || cp > 127) return false;
  }
  return true;
}

/**
 * Type guard to check if an error is an abort error.
 * Abort errors occur when a fetch request is cancelled via AbortController.
 *
 * @param {unknown} error the error to check
 * @returns {boolean} true if the error is an abort error
 */
export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

/**
 * Convert an error to a string array.
 *
 * @param {any} error the error message or object
 * @param {string} defaultMessage the default error message
 * @returns {string[]} the error messages
 */
export function formatErrorToStringArray(error: any, defaultMessage?: string): string[] {
  if (error instanceof Error) {
    return [error.message];
  }

  if (Array.isArray(error)) {
    return error.map((err) => {
      if (typeof err === "string") return err;
      if (err && typeof err === "object" && "message" in err) {
        return String((err as { message: string }).message);
      }
      return String(err);
    });
  }

  if (defaultMessage) {
    return [defaultMessage];
  } else {
    return ["An unexpected error occurred. Please try again."]
  }
}

export function formatErrorAsString(error: unknown, defaultMessage?: string): string {
  if (Array.isArray(error)) {
    if (error.length === 0) {
      return defaultMessage ?? "An unexpected error occurred. Please try again.";
    }
    return error
      .map((err) => {
        if (typeof err === "string") return err;
        if (err && typeof err === "object" && "message" in err) {
          return String((err as { message: string }).message);
        }
        return String(err);
      })
      .join(", ");
  } else if (typeof error === "string") {
    return error;
  } else {
    return defaultMessage ?? "An unexpected error occurred. Please try again.";
  }
}

/**
 * MUI `helperText` for a TanStack Form field's validation errors.
 *
 * `field.state.meta.errors` is an array (often `{ message: string }` from Zod).
 * MUI wants one string. Valid fields have an empty array and should show no
 * helper text, so the default is an empty string.
 *
 * @example
 * <TextField
 *   error={field.state.meta.errors.length > 0}
 *   helperText={formFieldHelperText(field.state.meta.errors)}
 * />
 */
export function formFieldHelperText(errors: unknown[]): string {
  return formatErrorAsString(errors, "");
}

/** One helper text per field that has errors, for a form's error summary */
export function formFieldErrorMessages(
  fieldErrors: Record<string, unknown> | undefined,
): string[] {
  return Object.values(fieldErrors ?? {})
    .map((errors) =>
      formFieldHelperText(Array.isArray(errors) ? errors : [errors]),
    )
    .filter(Boolean);
}

/** For UI that expects +string[]+ (e.g. snackbar +messages+) from API +errors+ that may be +{ type, message }[]+. */
export function apiErrorsToMessageStrings(
  errors: readonly (string | { message: string })[] | undefined | null,
): string[] {
  if (!errors?.length) {
    return [];
  }
  return errors.map((e) =>
    typeof e === "string" ? e : String((e as { message: string }).message),
  );
}

/**
 * Format a partial phone number as a US phone number, as much as is possible.
 * Removes all non-digit characters, before shortening input to max length of 10
 * and applying formatting. Returns "" if input is not a String.
 * Examples:
 *
 * - "55555555559" becomes "(555) 555-5555"
 * - "5555555555" becomes "(555) 555-5555"
 * - "555555555" becomes "(555) 555-555"
 * - "123123" becomes "(123) 123"
 * - "1231" becomes "(123) 1"
 * - "123" becomes "(123)"
 * - "12" becomes "12"
 *
 * @param {string} input input from text field
 * @returns {string} input formatted as a 10-digit US phone number
 */
export function formatPartialInputAsUSPhone(input: unknown): string {
  if (typeof input !== "string") {
    return ""
  }

  let formatted = input.replace(/\D/g, "")
  if (formatted.length > 10) {
    formatted = formatted.substring(0, 10)
  }

  if (formatted.length >= 7) {
    formatted = `(${formatted.substring(0, 3)}) ${formatted.substring(3, 6)}-${formatted.substring(6)}`
  } else if (formatted.length === 6) {
    formatted = `(${formatted.substring(0, 3)}) ${formatted.substring(3, 6)}`
  } else if (formatted.length >= 4) {
    formatted = `(${formatted.substring(0, 3)}) ${formatted.substring(3)}`
  } else if (formatted.length === 3) {
    formatted = `(${formatted})`
  }

  return formatted
}

/**
 * US date format with UTC timezone, and no zero padding for day or month.
 */
const UTC_US_DATE_FORMAT_NO_ZERO_PADDING = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  day: "numeric",
  month: "numeric",
  year: "numeric",
})

/**
 * US format a UTC date, preserving the date in UTC timezone.
 *
 * @param {Date | null | undefined} date The date to format.
 * @returns {string | null} The formatted date string, null if date is falsey.
 */
export function utcDateToUSDateString(date: Date | null | undefined): string | null {
  if (!date) {
    return null
  }

  return UTC_US_DATE_FORMAT_NO_ZERO_PADDING.format(date)
}

/** The value, or an em dash when it is null, undefined or blank */
export function emdashOrValue(value: string | number | null | undefined): string | number {
  return value === null || value === undefined || value === "" ? "—" : value;
}

const ZONE_NAME_FORMATS = new Map<string, Intl.DateTimeFormat>();

/** Time zone name formatter, "EST", cached per zone for zoneAbbreviation */
function zoneNameFormat(timeZone: string): Intl.DateTimeFormat {
  let format = ZONE_NAME_FORMATS.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    });
    ZONE_NAME_FORMATS.set(timeZone, format);
  }
  return format;
}

/** Formats a 24 hour time of day, "09:00" as "9:00 AM" */
export function formatTimeOfDay(value: string): string {
  const parsed = parseTimeOfDay(value);
  if (!parsed) return value;
  const suffix = parsed.hour >= 12 ? "PM" : "AM";
  const twelveHour = parsed.hour % 12 === 0 ? 12 : parsed.hour % 12;
  return `${twelveHour}:${String(parsed.minute).padStart(2, "0")} ${suffix}`;
}

/** Zone abbreviation, "EST", for a zone at a point in time */
export function zoneAbbreviation(timeZone: string, at: Date = new Date()): string {
  return (
    zoneNameFormat(timeZone)
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value ?? ""
  );
}

/**
 * Returns today's local calendar date in ISO format: YYYY-MM-DD.
 */
export function currentLocalDateISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Calendar date "today" as YYYY-MM-DD in `timeZone` (falls back to the browser zone).
 * Use when a date bound must follow a specific IANA zone, not the client local zone.
 * en-CA is the Intl locale that yields ISO-style YYYY-MM-DD.
 */
export function todayInTimeZone(timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      ...(timeZone ? { timeZone } : {}),
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Intl.DateTimeFormat("en-CA").format(new Date());
  }
}

// Pair this with a NullableField component to display a duration in a readable format
export function formatDuration(seconds: number | null): string | null {
  if (seconds === null || seconds < 0) return null;

  const duration = moment.duration(seconds, "seconds");

  const days = Math.floor(duration.asDays());
  const hrs = duration.hours();
  const mins = duration.minutes();
  const secs = duration.seconds();

  const parts: string[] = [];

  if (days > 0) parts.push(`${days}d`);
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
}

